import IconButton from '@hud/web-components/DesignSystem/Components/IconButton/IconButton';
import Button from '@hud/web-components/DesignSystem/Components/Button/Button';
import NotificationBanner from '@hud/web-components/DesignSystem/Components/NotificationBanner/NotificationBanner';
import { Pills } from '@hud/web-components/DesignSystem/Components/Pills/Pills';
import Select from '@hud/web-components/DesignSystem/Components/Select/Select';
import { StyledSetChannelDialog } from './SetChannelDialog.styles';
import { Channel, isSlackChannel } from '@hud/portal-dtos/channels.dtos';
import { SlackChannel } from '@hud/slack-dtos/api';

export const OVERRIDDEN_CHANNEL_TOOLTIP_TEXT = (channelName: string) =>
  `This service already has channel #${channelName}. Selecting a different channel will override this selection.`;

export const OVERRIDDEN_CHANNEL_BANNER_TEXT =
  'One or more of the selected Services already have Slack channel configured. Selecting a different channel will override this selection.';

export const DEFAULT_CHANNEL_BANNER_TEXT =
  'Private channels are visible only if the Hud Slack App is added.';

export const SLACK_APP_NOT_IN_CHANNEL_BANNER_TEXT =
  "The Hud Slack App isn't in this channel. Add it to ensure Alerts are delivered.";

type SetChannelDialogProps = {
  opened: boolean;
  selectedChannels: Channel[];
  slackChannels: SlackChannel[];
  selectedSlackChannel: SlackChannel | null;
  isLoadingChannels: boolean;
  onSlackChannelChange: (channel: SlackChannel | null) => void;
  onCheckAgainIsMemberOfSlackChannel: () => void;
  onDelete?: () => void;
  onClose: () => void;
  onSave: (selectedChannel: SlackChannel | null) => void;
};

const SetChannelDialog: React.FC<SetChannelDialogProps> = ({
  opened,
  onClose,
  onSave,
  selectedChannels,
  slackChannels,
  isLoadingChannels,
  selectedSlackChannel,
  onCheckAgainIsMemberOfSlackChannel,
  onSlackChannelChange,
  onDelete,
}) => {
  const pillItems = selectedChannels.map((channel) => {
    const warning =
      isSlackChannel(channel) && channel.config.channelId !== selectedSlackChannel?.id
        ? OVERRIDDEN_CHANNEL_TOOLTIP_TEXT(channel.config.channelName)
        : undefined;

    return {
      id: `${channel.serviceId}`,
      label: channel.serviceName || `Service ${channel.serviceId}`,
      warningTooltip: warning,
    };
  });
  const hasOverridingChannel = selectedChannels.some(
    (channel) =>
      isSlackChannel(channel) && channel.config.channelId !== selectedSlackChannel?.id,
  );

  return (
    <StyledSetChannelDialog opened={opened} onClose={onClose}>
      <div className="modal-header">
        <h2 className="modal-title">Set notification channel</h2>
        <IconButton icon="designSystem.xmark" onClick={onClose} />
      </div>
      <div className="modal-content">
        <div className="form">
          <div className="form-section">
            <label className="form-label" id="services-to-connect-label">
              Services to connect
            </label>
            <Pills
              prefixIcon="designSystem.service"
              items={pillItems}
              aria-label="Services selected for channel connection"
              aria-describedby="services-to-connect-label"
            />
          </div>

          <div className="form-section">
            <label className="form-label" htmlFor="slack-channel-select">
              Select a Slack channel
            </label>
            <Select
              searchable
              options={slackChannels.map((channel) => ({
                value: channel.id,
                label: `#${channel.name}`,
              }))}
              prefixIcon="designSystem.hashtag"
              selectedOption={
                selectedSlackChannel
                  ? {
                      value: selectedSlackChannel.id,
                      label: `#${selectedSlackChannel.name}`,
                      selectedLabel: selectedSlackChannel.name,
                    }
                  : undefined
              }
              warning={selectedSlackChannel?.isMember === false}
              onSelect={(option) =>
                onSlackChannelChange(
                  slackChannels.find((channel) => channel.id === option.value) || null,
                )
              }
              loading={isLoadingChannels}
            />
          </div>
        </div>
        {hasOverridingChannel ? (
          <NotificationBanner
            skin="warning"
            aria-label={`warning: ${OVERRIDDEN_CHANNEL_BANNER_TEXT}`}
          >
            {OVERRIDDEN_CHANNEL_BANNER_TEXT}
          </NotificationBanner>
        ) : null}
        {selectedSlackChannel?.isMember === false && (
          <NotificationBanner
            aria-label={`warning: ${SLACK_APP_NOT_IN_CHANNEL_BANNER_TEXT}`}
            skin="warning"
            suffix={
              <Button
                size="tiny"
                skin="warning"
                onClick={onCheckAgainIsMemberOfSlackChannel}
              >
                Check again
              </Button>
            }
          >
            {SLACK_APP_NOT_IN_CHANNEL_BANNER_TEXT}
          </NotificationBanner>
        )}
        {!hasOverridingChannel && selectedSlackChannel?.isMember !== false && (
          <NotificationBanner skin="default">
            {DEFAULT_CHANNEL_BANNER_TEXT}
          </NotificationBanner>
        )}
      </div>
      <div className="modal-actions">
        {onDelete && (
          <Button className="delete" size="small" skin="destructive" onClick={onDelete}>
            Delete
          </Button>
        )}
        <Button size="small" skin="default" onClick={onClose} className="cancel">
          Cancel
        </Button>
        <Button
          className="save"
          size="small"
          skin="default"
          onClick={() =>
            onSave(
              selectedSlackChannel
                ? slackChannels.find(
                    (channel) => channel.id === selectedSlackChannel.id,
                  ) || null
                : null,
            )
          }
          disabled={!selectedSlackChannel}
        >
          Save
        </Button>
      </div>
    </StyledSetChannelDialog>
  );
};

export default SetChannelDialog;
